import React, { useCallback, useEffect, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import api from '@/lib/api';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { canSeeAggregateMoney } from '@/lib/constants';
import { MapPin, Clock, TruckIcon, AlertTriangle, Gauge } from 'lucide-react';
import ProductionSchedule from './Reports/ProductionSchedule';

import { Screen, ScreenSplit, Pane, CommandBar } from '@/components/data';

const money = (v) => `₹${Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const count = (v) => Number(v || 0).toLocaleString();

function Table({ head, children, empty }) {
  return (
    <div className="overflow-x-auto rounded-lg border border-slate-200">
      <table className="w-full text-xs">
        <thead className="bg-slate-100 text-[10px] uppercase tracking-wider text-slate-500">
          <tr>{head.map(h => <th key={h} className="px-2.5 py-2 text-left whitespace-nowrap">{h}</th>)}</tr>
        </thead>
        <tbody>
          {React.Children.count(children) === 0
            ? <tr><td colSpan={head.length} className="px-2 py-10 text-center text-slate-400">{empty}</td></tr>
            : children}
        </tbody>
      </table>
    </div>
  );
}

function Stat({ label, value, tone }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3">
      <p className="text-[10px] uppercase tracking-wider text-slate-400">{label}</p>
      <p className={`text-xl font-bold tabular-nums ${tone || 'text-slate-800'}`}>{value}</p>
    </div>
  );
}

/**
 * Status board — where everything is.
 *
 * A job at Die Cutting and a job at Packing are both `in_production`, and that
 * difference is the only thing anyone on the floor wants to know. Process rows
 * come first because they are the answer; lifecycle rows follow as context.
 */
function StatusBoard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get('/reports/status-board').then(({ data }) => setData(data))
      .catch(() => toast.error('Could not load the status board'));
  }, []);

  if (!data) return <p className="py-10 text-center text-sm text-slate-400">Loading…</p>;

  const processRows = data.rows.filter(r => r.kind === 'process');
  const lifecycleRows = data.rows.filter(r => r.kind === 'lifecycle');
  // the server drops the value off the rows for roles that may not see it,
  // rather than sending a number the page is trusted to hide
  const showValue = data.totals.value != null;
  const head = ['Where it is', 'Orders', 'Quantity', 'Plan qty', ...(showValue ? ['Value'] : [])];

  const section = (title, rows, tone) => rows.length > 0 && (
    <>
      <tr className="bg-slate-50">
        <td colSpan={head.length} className="px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
          {title}
        </td>
      </tr>
      {rows.map(row => (
        <tr key={row.status} className="border-t border-slate-200">
          <td className="px-2.5 py-2">
            <Badge className={`text-[10px] ${tone}`}>{row.status}</Badge>
          </td>
          <td className="px-2.5 py-2 text-right tabular-nums">{count(row.orders)}</td>
          <td className="px-2.5 py-2 text-right tabular-nums">{count(row.quantity)}</td>
          <td className="px-2.5 py-2 text-right tabular-nums text-slate-500">{count(row.plan_quantity)}</td>
          {showValue && <td className="px-2.5 py-2 text-right tabular-nums font-medium">{money(row.value)}</td>}
        </tr>
      ))}
    </>
  );

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        <Stat label="Orders" value={count(data.totals.orders)} />
        <Stat label="Quantity" value={count(data.totals.quantity)} />
        {showValue && <Stat label="Value" value={money(data.totals.value)} />}
      </div>
      <Table head={head} empty="Nothing in the system yet.">
        {section('On the floor', processRows, 'bg-emerald-50 text-emerald-700')}
        {section('Before and after production', lifecycleRows, 'bg-slate-100 text-slate-600')}
      </Table>
    </div>
  );
}

function PendingOrders() {
  const [data, setData] = useState(null);
  const [overdueOnly, setOverdueOnly] = useState(false);

  const load = useCallback(() => {
    api.get('/reports/pending-sales-orders', { params: { overdue_only: overdueOnly, limit: 200 } })
      .then(({ data }) => setData(data))
      .catch(() => toast.error('Could not load pending orders'));
  }, [overdueOnly]);

  useEffect(load, [load]);
  if (!data) return <p className="py-10 text-center text-sm text-slate-400">Loading…</p>;

  const showValue = data.totals.pending_value != null;

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Stat label="Pending qty" value={count(data.totals.pending_qty)} />
        {showValue && <Stat label="Pending value" value={money(data.totals.pending_value)} />}
        {showValue && <Stat label="Outstanding" value={money(data.totals.outstanding)} />}
        <Stat label="Overdue" value={count(data.totals.overdue_orders)}
          tone={data.totals.overdue_orders > 0 ? 'text-red-600' : undefined} />
      </div>
      <Button size="sm" variant={overdueOnly ? 'default' : 'outline'} onClick={() => setOverdueOnly(v => !v)}>
        <AlertTriangle className="h-3.5 w-3.5 mr-1" /> {overdueOnly ? 'Showing overdue only' : 'Overdue only'}
      </Button>
      <Table head={['Order', 'Customer', 'Where it is', 'Due', 'Days', 'Ordered', 'Pending',
        ...(showValue ? ['Pending value', 'Outstanding'] : [])]}
        empty="Nothing pending.">
        {data.rows.map(row => (
          <tr key={row.order_id} className="border-t border-slate-200">
            <td className="px-2.5 py-2 font-mono text-[11px]">{row.order_id}</td>
            <td className="px-2.5 py-2">{row.customer_name}{row.firm_name ? ` · ${row.firm_name}` : ''}</td>
            <td className="px-2.5 py-2 text-slate-500">{row.location}</td>
            <td className="px-2.5 py-2">{row.due_date?.slice(0, 10) || '—'}</td>
            <td className={`px-2.5 py-2 text-right tabular-nums ${row.overdue ? 'text-red-600 font-semibold' : ''}`}>
              {row.days_to_due === null ? '—' : row.days_to_due < 0 ? `${Math.abs(row.days_to_due)} late` : row.days_to_due}
            </td>
            <td className="px-2.5 py-2 text-right tabular-nums">{count(row.ordered_qty)}</td>
            <td className="px-2.5 py-2 text-right tabular-nums font-medium">{count(row.pending_qty)}</td>
            {showValue && <td className="px-2.5 py-2 text-right tabular-nums">{money(row.pending_value)}</td>}
            {showValue && <td className="px-2.5 py-2 text-right tabular-nums">{money(row.outstanding)}</td>}
          </tr>
        ))}
      </Table>
    </div>
  );
}

function Ageing() {
  const [data, setData] = useState(null);
  const [basis, setBasis] = useState('receivable');

  useEffect(() => {
    api.get('/reports/ageing', { params: { basis } }).then(({ data }) => setData(data))
      .catch(() => toast.error('Could not load the ageing report'));
  }, [basis]);

  if (!data) return <p className="py-10 text-center text-sm text-slate-400">Loading…</p>;

  return (
    <div className="space-y-3">
      <div className="flex gap-2">
        {[['receivable', 'Money owed'], ['order', 'Orders open']].map(([value, label]) => (
          <Button key={value} size="sm" variant={basis === value ? 'default' : 'outline'} onClick={() => setBasis(value)}>
            {label}
          </Button>
        ))}
      </div>
      <div className="grid grid-cols-3 gap-3 md:grid-cols-6">
        {data.buckets.map(b => (
          <Stat key={b.bucket} label={b.bucket} value={money(b.amount)} />
        ))}
      </div>
      <Table head={['Order', 'Customer', 'Order date', 'Age', 'Bucket', 'Value', 'Paid', basis === 'receivable' ? 'Owed' : 'Amount']}
        empty="Nothing outstanding.">
        {data.rows.map(row => (
          <tr key={row.order_id} className="border-t border-slate-200">
            <td className="px-2.5 py-2 font-mono text-[11px]">{row.order_id}</td>
            <td className="px-2.5 py-2">{row.customer_name}</td>
            <td className="px-2.5 py-2">{row.order_date?.slice(0, 10)}</td>
            <td className="px-2.5 py-2 text-right tabular-nums">{row.age_days}d</td>
            <td className="px-2.5 py-2"><Badge variant="outline" className="text-[10px]">{row.bucket}</Badge></td>
            <td className="px-2.5 py-2 text-right tabular-nums">{money(row.order_value)}</td>
            <td className="px-2.5 py-2 text-right tabular-nums text-slate-500">{money(row.paid)}</td>
            <td className="px-2.5 py-2 text-right tabular-nums font-medium">{money(row.amount)}</td>
          </tr>
        ))}
      </Table>
    </div>
  );
}

function PoVsDispatch() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get('/reports/po-vs-dispatch').then(({ data }) => setData(data))
      .catch(() => toast.error('Could not load the dispatch comparison'));
  }, []);

  if (!data) return <p className="py-10 text-center text-sm text-slate-400">Loading…</p>;

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Stat label="On time" value={count(data.summary.on_time)} tone="text-emerald-600" />
        <Stat label="Late" value={count(data.summary.late)} tone={data.summary.late > 0 ? 'text-red-600' : undefined} />
        <Stat label="On-time rate" value={`${data.summary.on_time_pct}%`} />
        <Stat label="Avg days late" value={data.summary.avg_days_late} />
      </div>
      <Table head={['Order', 'Customer', 'PO ref', 'Due', 'Dispatched', 'Ordered', 'Dispatched qty', 'Variance', 'Days late']}
        empty="Nothing has been dispatched in this window.">
        {data.rows.map(row => (
          <tr key={row.order_id} className="border-t border-slate-200">
            <td className="px-2.5 py-2 font-mono text-[11px]">{row.order_id}</td>
            <td className="px-2.5 py-2">{row.customer_name}</td>
            <td className="px-2.5 py-2 text-slate-500">{row.po_ref || '—'}</td>
            <td className="px-2.5 py-2">{row.due_date?.slice(0, 10) || '—'}</td>
            <td className="px-2.5 py-2">{row.dispatched_at?.slice(0, 10) || '—'}</td>
            <td className="px-2.5 py-2 text-right tabular-nums">{count(row.ordered_qty)}</td>
            <td className="px-2.5 py-2 text-right tabular-nums">{count(row.dispatched_qty)}</td>
            <td className={`px-2.5 py-2 text-right tabular-nums ${row.qty_variance < 0 ? 'text-red-600' : ''}`}>
              {row.qty_variance > 0 ? `+${row.qty_variance}` : row.qty_variance}
            </td>
            <td className={`px-2.5 py-2 text-right tabular-nums ${row.on_time ? 'text-emerald-600' : 'text-red-600 font-semibold'}`}>
              {row.days_late === null ? '—' : row.days_late <= 0 ? 'on time' : row.days_late}
            </td>
          </tr>
        ))}
      </Table>
    </div>
  );
}

/**
 * Picking a report is a rail; reading it is the screen — the set is grouped
 * rather than sat on one row, because a tab bar does not survive the set
 * growing.
 */
// `money: true` marks a report the API will refuse for anyone outside
// AGGREGATE_MONEY_ROLES. The floor reports stay for the floor; the value
// columns inside them are dropped server-side, so those render without them.
const REPORTS = [
  { group: 'Where things are', items: [
    { key: 'status', label: 'Status Board', icon: MapPin, render: () => <StatusBoard /> },
    { key: 'pending', label: 'Pending Orders', icon: Clock, render: () => <PendingOrders /> },
    { key: 'schedule', label: 'Machine Load', icon: Gauge, render: () => <ProductionSchedule /> },
  ] },
  { group: 'Late and out', items: [
    { key: 'ageing', label: 'Ageing', icon: AlertTriangle, money: true, render: () => <Ageing /> },
    { key: 'dispatch', label: 'PO vs Dispatch', icon: TruckIcon, render: () => <PoVsDispatch /> },
  ] },
];

export default function ReportsPage() {
  const { user } = useAuth();
  const showMoney = canSeeAggregateMoney(user);

  const groups = REPORTS
    .map(g => ({ ...g, items: g.items.filter(r => showMoney || !r.money) }))
    .filter(g => g.items.length);
  const allReports = groups.flatMap(g => g.items);

  const [active, setActive] = useState('status');
  const report = allReports.find(r => r.key === active) || allReports[0];

  return (
    <Screen data-testid="reports-page">
      <CommandBar
        title="Reports"
        subtitle={report?.label}
      />

      <ScreenSplit
        railWidth="14rem"
        rail={
          <Pane title="Reports" aside={`${allReports.length}`} className="max-h-full">
            {/* A vertical tab list: one report is shown at a time, and the rail
                is how you choose which. */}
            <div role="tablist" aria-orientation="vertical" aria-label="Reports">
              {groups.map(group => (
                <div key={group.group}>
                  <p className="border-b border-slate-100 bg-slate-50 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                    {group.group}
                  </p>
                  {group.items.map(item => {
                    const Icon = item.icon;
                    const on = item.key === active;
                    return (
                      <button
                        key={item.key}
                        type="button"
                        role="tab"
                        id={`report-tab-${item.key}`}
                        aria-selected={on}
                        aria-controls="report-panel"
                        tabIndex={on ? 0 : -1}
                        onClick={() => setActive(item.key)}
                        className={`flex w-full items-center gap-2 border-b border-slate-100 px-2.5 py-1.5 text-left text-[13px] transition-colors last:border-b-0 ${
                          on ? 'bg-brand-blue/10 font-semibold text-brand-blue' : 'text-slate-600 hover:bg-slate-50'
                        }`}
                      >
                        <Icon className="h-3.5 w-3.5 shrink-0" />
                        <span className="truncate">{item.label}</span>
                      </button>
                    );
                  })}
                </div>
              ))}
            </div>
          </Pane>
        }
      >
        <Pane title={report.label} bodyClassName="p-3">
          <div role="tabpanel" id="report-panel" aria-labelledby={`report-tab-${report.key}`}>
            {report.render()}
          </div>
        </Pane>
      </ScreenSplit>
    </Screen>
  );
}
